import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  DataAccessEventsFacade,
  DataAccessEventsPageFacade,
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  Fair,
  KeyedExhibitorsState,
} from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { USER_SESSION_STATUS } from '@planone/bau365/data-access-user';
import { BreadcrumbHelper } from '@planone/bau365/feature-breadcrumb';
import {
  LinkClickEventType,
  LinkClickFacade,
  ShareButtonClickEventType,
  ShareDialogOpenEventType,
  ShareFacade,
} from '@planone/bau365/feature-tracking';
import { Event, Exhibitor } from '@planone/bau365/util-fair-data';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import { BreadcrumbElement, ShareButtonsHelper } from '@planone/shared/ui-elements';
import { WindowRef } from '@planone/shared/util-browser-api';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { BreadcrumbData, BreadcrumbGenerator } from '@planone/shared/util-structured-data';
import { BehaviorSubject, combineLatest, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';

@Component({
  selector: 'bau365-page-event-details',
  templateUrl: './page-event-details.component.html',
  styleUrls: ['./page-event-details.component.scss'],
})
export class PageEventDetailsComponent implements OnInit, OnDestroy {
  event$: Observable<Event | undefined>;
  filteredEventList$: Observable<Event[] | undefined>;
  eventLoading$: Observable<boolean | undefined>;
  eventError$: Observable<string | undefined>;
  exhibitorDirectory$: Observable<KeyedExhibitorsState>;
  fair$: Observable<Fair | undefined>;
  fairId$: Observable<string>;
  size$: Observable<string | undefined>;
  isBauInsights$: Observable<boolean>;

  private unsubscribe: Subject<void> = new Subject<void>();

  socialShareUrl: string | undefined;
  breadcrumbElements: BreadcrumbElement[] | undefined;
  exhibitor: Exhibitor | undefined;
  emailSubject = localizedMeta['shareEmailSubject'];
  emailBody = localizedMeta['shareEmailBody'];

  constructor(
    private EventsFacade: DataAccessEventsFacade,
    private EventsPageFacade: DataAccessEventsPageFacade,
    private FairsFacade: DataAccessFairsFacade,
    private ExhibitorsFacade: DataAccessExhibitorsFacade,
    private ScreensizeFacade: DataAccessScreensizeFacade,
    private shareButtonsHelper: ShareButtonsHelper,
    private activatedRoute: ActivatedRoute,
    private structuredDataBreadcrumbGenerator: BreadcrumbGenerator,
    private renderer2: Renderer2,
    private metaService: MetaService,
    private breadcrumbTransformer: BreadcrumbHelper,
    private windowRef: WindowRef,
    @Inject(BOT_REGEX) private botRegex: RegExp,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(USER_SESSION_STATUS)
    private userSessionStatus: BehaviorSubject<boolean>,
    @Inject(PLATFORM_ID) private platformId: string,
    private trackingShareFacade: ShareFacade,
    private trackingLinkClickFacade: LinkClickFacade
  ) {
    this.metaService.setTitle(localizedMeta['titleEventDetails'], [$localize`:@@pageMeta-titles-loading: Lädt...`]);

    this.event$ = EventsFacade.event$;
    this.filteredEventList$ = EventsFacade.filteredEventList$;
    this.eventLoading$ = EventsFacade.eventLoading$;
    this.eventError$ = EventsFacade.eventError$;
    this.exhibitorDirectory$ = ExhibitorsFacade.exhibitorDirectory$;
    this.fair$ = FairsFacade.fair$;
    this.fairId$ = FairsFacade.fairId$;
    this.size$ = ScreensizeFacade.size$;
    this.isBauInsights$ = FairsFacade.isBauInsights$;
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      this.EventsFacade.pageOpened(params['id']);
    });

    this.EventsPageFacade.pageView(
      this.activatedRoute.snapshot.params['id'],
      this.activatedRoute.snapshot.params['fair_id']
    );

    this.FairsFacade.fairConfig$.pipe(takeUntil(this.unsubscribe)).subscribe((fair) => {
      this.metaService.removeOpenGraphMetaTags();
      this.metaService.addOpenGraphMetaTags(
        localizedMeta['linkPreviewTitleLandingPage'],
        localizedMeta['linkPreviewDescriptionLandingPage'],
        this.document.URL,
        this.baseHref + 'assets/link-preview-img.png',
        [fair.name]
      );
    });

    this.event$
      .pipe(withLatestFrom(this.FairsFacade.fairId$), takeUntil(this.unsubscribe))
      .subscribe(([event, fairId]) => {
        if (event) {
          this.socialShareUrl = this.shareButtonsHelper.buildShareUrlFromPath(
            'fairs/' + (event?.fair_id ?? fairId) + '/events/' + event.id
          );

          this.emailBody += $localize`:@@pageEvent-emailShare-entityType:Veranstaltung ${event?.headline}`;
        }
      });

    combineLatest(this.event$, this.exhibitorDirectory$)
      .pipe(withLatestFrom(this.FairsFacade.fairConfig$), takeUntil(this.unsubscribe))
      .subscribe(([[event, directory], fair]) => {
        if (event?.exhibitor_id && directory[event?.exhibitor_id]) {
          this.exhibitor = directory[event?.exhibitor_id];

          const breadcrumbData: BreadcrumbData = this.breadcrumbTransformer.prepareForEvent(
            fair,
            this.exhibitor,
            event
          );

          this.breadcrumbElements = breadcrumbData.elements;

          this.structuredDataBreadcrumbGenerator.validateBreadcrumbStructuredData(
            this.renderer2,
            breadcrumbData.structuredData,
            this.document.head
          );
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();

    this.metaService.removeOpenGraphMetaTags();
    this.structuredDataBreadcrumbGenerator.removeTag(this.renderer2, this.document.head);
  }

  trackShareDialogOpened(event: ShareDialogOpenEventType) {
    this.trackingShareFacade.open(event);
  }

  trackShareDialogClicked(event: ShareButtonClickEventType) {
    this.trackingShareFacade.click(event);
  }

  trackLinkClicked(event: LinkClickEventType) {
    this.trackingLinkClickFacade.trigger(event);
  }
}
